import React, { Component, ReactNode } from "react"
import { connect } from "react-redux"
import { Navigate, Outlet, Route, Routes } from "react-router"
import { fetchInstallation } from "../services/client/actions"
import IsThisYourAccount from "../pages/IsThisYourAccount/IsThisYourAccount"

import {
  Home,
  Loading,
  NotFound,
  NotAuthorized,
  Maintenance,
  Admin,
  InsertRelative,
  Validator,
  Verify,
  Authentication,
} from "../pages"
import {
  CHECK_PHONE,
  CHECK_LASTNAME,
  IS_THIS_YOUR_ACCOUNT,
  HOME_PAGE,
  ADMIN_PAGE,
  NOT_AUTHORIZED_PAGE,
  WELCOME_PAGE,
  VERIFY_PAGE,
  LOBBY_PAGE,
  SURVEY_PAGE,
  INSERT_RELATIVE_PAGE,
  FINALE_PAGE,
  CHECK_PASSWORD,
  REGISTER_EMAIL,
  REGISTER_PERSONAL_DATA,
  VERIFY_PHONE,
  VERIFY_LOGIN,
  RETRIEVE_ACCOUNT,
  MAINTENANCE,
  SET_RELATIVE_PAGE,
  CURRENT_CONSULTATIONS_PAGE,
} from "./constants"
import SetRelativeGender from "pages/InsertRelative/setRelativeGender"
import { ClientStore } from "../types/types"
import Lobby from "../pages/Lobby/Lobby"
import "./App.scss"
import Finale from "../pages/Finale/Finale"
import config from "react-global-configuration"
import Survey from "../pages/Survey/Survey"
import CurrentConsultations from "pages/currentConsultations/CurrentConsultations"

interface RootProps {
  fetchInstallation: () => void
  success: boolean
  admin: boolean
  login: boolean
  loading: boolean
  verified: boolean
  error: string | number | null
}

interface ProtectedRouteProps {
  isAllowed: boolean
  redirectPath: string
  children?: ReactNode
}

const ProtectedRoute = ({
  isAllowed,
  redirectPath,
  children,
}: ProtectedRouteProps) => {
  if (!isAllowed) {
    return <Navigate to={redirectPath} replace />
  }
  return children ? <>{children}</> : <Outlet />
}

class Root extends Component<RootProps> {
  constructor(props: RootProps) {
    super(props)
    this.props.fetchInstallation()
  }

  state = { shouldRefresh: false }

  timerUpdate = () => {
    const refreshAppEveryInMs: number =
      config.get("refreshAppEveryInMs") || 3600000
    const id = setTimeout(
      () => {
        this.setState({ shouldRefresh: true })
      },
      refreshAppEveryInMs // [timeOutHour] hour(s) in milliseconds
    )
    return () => clearTimeout(id)
  }

  componentDidMount = () => {
    // on lance le timer à l'initialisation
    // objectif : toutes les [timeOutHour] heures, mettre shouldRefresh à true
    this.timerUpdate()
  }

  componentDidUpdate = (
    prevProps: any,
    prevState: { shouldRefresh: boolean }
  ) => {
    const triggerOn = [HOME_PAGE].includes(window.location.pathname)
    // Si shouldRefresh est true
    if (this.state.shouldRefresh && !prevState.shouldRefresh) {
      if (!this.props.login && triggerOn) {
        // Et que aucun patient n'est connecté, et que l'on
        //  n'est pas sur une page critique,
        // on peut rafraichir
        console.log(
          "%c --- Automatic reload | [ Update App ]",
          "background: #222; color: #bada55"
        )
        document.location.reload()
      } else {
        // Sinon, on relance un timer, en espérant que la prochaine fois,
        // aucun patient ne soit connecté pour rafraichîr
        this.setState({ shouldRefresh: false })
      }
    }
    // Si le timer s'est désactivé à l'update précédent, on lance le timer
    else if (!this.state.shouldRefresh && prevState.shouldRefresh) {
      this.timerUpdate()
    }
  }

  public render = (): ReactNode => {
    const { admin, login, verified } = this.props

    return this.props.loading ? (
      <Loading></Loading>
    ) : !this.props.success && window.location.pathname !== MAINTENANCE ? (
      <NotAuthorized />
    ) : (
      <Routes>
        <Route path={MAINTENANCE} element={<Maintenance />} />
        <Route path={NOT_AUTHORIZED_PAGE} element={<NotAuthorized />} />

        {/* Installation identified (admin) */}
        <Route
          element={
            <ProtectedRoute isAllowed={admin} redirectPath={ADMIN_PAGE} />
          }
        >
          <Route path={WELCOME_PAGE} element={<Home />} />
          <Route path={HOME_PAGE} element={<Home />} />
          <Route path="/" element={<Home />} />
        </Route>

        {/* Installation not identified yet */}
        <Route
          element={
            <ProtectedRoute isAllowed={!admin} redirectPath={HOME_PAGE} />
          }
        >
          <Route path={ADMIN_PAGE} element={<Admin />} />
        </Route>

        {/* Installation identified, no patient logged in */}
        <Route
          element={
            <ProtectedRoute isAllowed={admin} redirectPath={ADMIN_PAGE} />
          }
        >
          <Route
            element={
              <ProtectedRoute isAllowed={!login} redirectPath={SURVEY_PAGE} />
            }
          >
            {[
              CHECK_PHONE,
              CHECK_PASSWORD,
              CHECK_LASTNAME,
              REGISTER_EMAIL,
              REGISTER_PERSONAL_DATA,
              VERIFY_PHONE,
              VERIFY_LOGIN,
            ].map((path) => (
              <Route key={path} path={path} element={<Validator />} />
            ))}
            <Route path={RETRIEVE_ACCOUNT} element={<Authentication />} />
            <Route path={LOBBY_PAGE} element={<Lobby />} />
          </Route>
        </Route>

        {/* Installation identified, patient logged in */}
        <Route
          element={
            <ProtectedRoute isAllowed={admin} redirectPath={ADMIN_PAGE} />
          }
        >
          <Route
            element={
              <ProtectedRoute isAllowed={login} redirectPath={LOBBY_PAGE} />
            }
          >
            <Route path={IS_THIS_YOUR_ACCOUNT} element={<IsThisYourAccount />} />
          </Route>
        </Route>

        {/* Patient logged in, unidentified installations are not authorized */}
        <Route
          element={
            <ProtectedRoute
              isAllowed={admin}
              redirectPath={NOT_AUTHORIZED_PAGE}
            />
          }
        >
          <Route
            element={
              <ProtectedRoute isAllowed={login} redirectPath={LOBBY_PAGE} />
            }
          >
            <Route path={SURVEY_PAGE} element={<Survey />} />
            <Route
              path={CURRENT_CONSULTATIONS_PAGE}
              element={<CurrentConsultations />}
            />
            <Route path={INSERT_RELATIVE_PAGE} element={<InsertRelative />} />
            <Route path={SET_RELATIVE_PAGE} element={<SetRelativeGender />} />
          </Route>
          {/* Finale logs the patient out on arrival, so it must not sit
              behind the reactive login guard or it unmounts immediately */}
          <Route path={FINALE_PAGE} element={<Finale />} />
        </Route>

        {/* Finale logs the patient out on arrival, so it must not require login */}
        <Route
          element={
            <ProtectedRoute
              isAllowed={admin}
              redirectPath={NOT_AUTHORIZED_PAGE}
            />
          }
        >
          <Route path={FINALE_PAGE} element={<Finale />} />
        </Route>

        {/* Patient logged in but not verified yet */}
        <Route
          element={
            <ProtectedRoute isAllowed={admin} redirectPath={ADMIN_PAGE} />
          }
        >
          <Route
            element={
              <ProtectedRoute isAllowed={login} redirectPath={LOBBY_PAGE} />
            }
          >
            <Route
              element={
                <ProtectedRoute
                  isAllowed={!verified}
                  redirectPath={SURVEY_PAGE}
                />
              }
            >
              <Route path={VERIFY_PAGE} element={<Verify />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    )
  }
}

export const mapStateToProps = ({
  client,
}: {
  client: ClientStore
}): Partial<RootProps> => {
  return {
    success: client.success,
    loading: client.loading,
    admin: !!client.clinic,
    login: !!client.patient,
    verified: client.patient?.is_verified ? true : false,
  }
}

const mapDispatchToProps = (dispatch: Function): Partial<RootProps> => {
  return {
    fetchInstallation: () => dispatch(fetchInstallation()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Root as any)
